/**
 * Scénarios prévisionnels modifiables.
 *
 * Un scénario est **autonome** : tout ce qui détermine une trajectoire lui
 * appartient — effectifs, produit moyen, ressources conventionnelles,
 * trajectoire salariale, progression des charges, lignes exceptionnelles. Seuls
 * restent globaux les données réelles et les constantes de configuration.
 *
 * Sans cela, « scénario 3 » ne désignerait plus une trajectoire mais une moitié
 * de trajectoire, et deux scénarios comparés en séance ne différeraient pas
 * seulement par ce qu'on croit lire. Voir
 * `docs/superpowers/specs/2026-08-08-administration-et-graphiques-design.md`.
 *
 * Les scénarios votés ne sont pas décrits ici : ils vivent dans
 * `configuration.ts`, sous contrôle de version, et restent en lecture seule.
 * Toute modification engendre un scénario **dérivé**, nommé et daté.
 */
import { AUDIENS, EXERCICES_TRAJECTOIRE, SCENARIO_3, poste } from './configuration.js';
import { sommeExacte } from './adherents.js';
import { resoudrePilote } from './pilote.js';
import { construireTrajectoire } from './scenarios.js';
/** Natures d'une ligne exceptionnelle. */
export const NATURES_EXCEPTIONNELLES = ['produit', 'charge', 'investissement'];
/**
 * Amortissement supporté par un exercice au titre d'une ligne exceptionnelle.
 * Un investissement non amorti (`dureeAmortissement` nul) passe intégralement
 * en charge l'année de l'acquisition.
 */
export function amortissementExercice(l, exercice) {
    if (l.nature !== 'investissement')
        return 0;
    if (l.dureeAmortissement <= 0)
        return exercice === l.exercice ? l.montant : 0;
    const rang = exercice - l.exercice;
    if (rang < 0 || rang >= l.dureeAmortissement)
        return 0;
    return Math.round((l.montant / l.dureeAmortissement) * 100) / 100;
}
/** Évalue une définition de scénario et rend la trajectoire correspondante. */
export function evaluerScenario(d, c) {
    const exercices = d.exercices;
    const adherents = resoudrePilote(d.adherents, exercices, c.payeursExerciceCourant).map((v) => Math.max(0, Math.round(v)));
    const produitsMoyens = resoudrePilote(d.produitMoyen, exercices, c.produitMoyenExerciceCourant);
    // Assiette des autres charges : les charges de l'exercice en cours diminuées
    // de la masse salariale. Elle ne vaut que pour le régime détaillé.
    const assietteAutresCharges = Math.max(0, sommeExacte([c.chargesExerciceCourant, -c.masseSalarialeExerciceCourant]));
    const detail = exercices.map((annee, i) => {
        const nbAdherents = adherents[i] ?? 0;
        const moyen = produitsMoyens[i] ?? 0;
        const cotisations = Math.round(nbAdherents * moyen * 100) / 100;
        const ressources = Object.entries(d.ressources).map(([code, pilote]) => {
            const serie = resoudrePilote(pilote, exercices, 0);
            // La convention Audiens s'éteint après sa dernière année d'application.
            const brut = code === 'audiens' && annee > AUDIENS.derniereAnnee ? 0 : (serie[i] ?? 0);
            return { code, libelle: poste(code)?.libelle ?? code, montant: brut };
        });
        const lignesAnnee = d.lignesExceptionnelles.filter((l) => l.exercice === annee);
        const produitsExceptionnels = sommeExacte(lignesAnnee.filter((l) => l.nature === 'produit').map((l) => l.montant));
        const chargesExceptionnelles = sommeExacte(lignesAnnee.filter((l) => l.nature === 'charge').map((l) => l.montant));
        const investissements = sommeExacte(lignesAnnee.filter((l) => l.nature === 'investissement').map((l) => l.montant));
        const amortissements = sommeExacte(d.lignesExceptionnelles.map((l) => amortissementExercice(l, annee)));
        let masseSalariale;
        let autresCharges;
        if (d.charges.mode === 'global') {
            // Régime global : la totalité des charges de l'exercice en cours est
            // indexée d'un taux unique, sans distinguer la paie du reste.
            const total = Math.round(c.chargesExerciceCourant * (1 + d.charges.progression) ** i * 100) / 100;
            masseSalariale = 0;
            autresCharges = total;
        }
        else {
            const taux = d.charges.progressionAutresCharges;
            masseSalariale =
                c.masseSalariale[annee] ??
                    Math.round(c.masseSalarialeExerciceCourant * (1 + taux) ** i * 100) / 100;
            autresCharges = Math.round(assietteAutresCharges * (1 + taux) ** i * 100) / 100;
        }
        const totalProduits = sommeExacte([
            cotisations,
            ...ressources.map((r) => r.montant),
            produitsExceptionnels,
        ]);
        const totalCharges = sommeExacte([
            masseSalariale,
            autresCharges,
            chargesExceptionnelles,
            amortissements,
        ]);
        return {
            exercice: annee,
            adherents: nbAdherents,
            produitMoyen: moyen,
            cotisations,
            ressources,
            produitsExceptionnels,
            totalProduits,
            masseSalariale,
            autresCharges,
            chargesExceptionnelles,
            amortissements,
            totalCharges,
            resultat: sommeExacte([totalProduits, -totalCharges]),
            investissements,
        };
    });
    const trajectoire = construireTrajectoire({
        cle: d.cle,
        libelle: d.libelle,
        annees: exercices,
        adherents: detail.map((x) => x.adherents),
        produits: detail.map((x) => x.totalProduits),
        charges: detail.map((x) => x.totalCharges),
        reserveDepart: c.reserveDepart,
        ...(c.seuils ? { seuils: c.seuils } : {}),
    });
    return { definition: d, trajectoire, detail };
}
/**
 * Définition reproduisant le scénario 3 tel qu'il est calculé aujourd'hui.
 *
 * Elle sert de point de départ à tout scénario dérivé : plutôt que de partir
 * d'une page blanche, la direction part de la projection de référence et
 * déplace ce qu'elle veut éprouver. Elle sert aussi de test de non-régression
 * du modèle — `evaluerScenario` doit rendre exactement `scenario3`.
 */
export function definitionDeReference(c) {
    const exercices = c.exercices ?? EXERCICES_TRAJECTOIRE;
    const premier = exercices[0] ?? 0;
    const dernier = exercices[exercices.length - 1] ?? premier;
    // Le produit moyen prolonge la pente observée : une série arbitrée exercice
    // par exercice, car une pente en euros n'est pas un taux.
    const produitsMoyens = Array.from({ length: exercices.length }, (_, i) => Math.round((c.produitMoyenExerciceCourant + c.pente * i) * 100) / 100);
    // Le premier exercice reprend les produits et charges projetés du réel :
    // les ressources conventionnelles y sont déjà comprises, et les cotisations
    // s'en déduisent par différence.
    const socle = Object.values(SCENARIO_3.ressourcesConventionnelles).reduce((s, v) => s + v, 0);
    const cotisationsPremierExercice = sommeExacte([
        c.produitsExerciceCourant,
        -socle,
        -(premier <= AUDIENS.derniereAnnee ? AUDIENS.montantAnnuel : 0),
    ]);
    const ressources = {};
    for (const [code, montant] of Object.entries(SCENARIO_3.ressourcesConventionnelles)) {
        ressources[code] = { mode: 'constant', valeur: montant };
    }
    ressources['audiens'] = { mode: 'constant', valeur: AUDIENS.montantAnnuel };
    return {
        cle: c.cle,
        libelle: c.libelle,
        parent: c.parent,
        exercices,
        adherents: { mode: 'cible', cible: SCENARIO_3.cible2030, exerciceCible: dernier },
        produitMoyen: {
            mode: 'annee_par_annee',
            valeurs: [
                // Le premier exercice n'est pas régi par la pente : il porte les
                // cotisations effectivement projetées, rapportées à l'effectif. La
                // division n'est pas arrondie à la centime — ce serait perdre des
                // dizaines de centimes une fois remultipliée par l'effectif.
                c.payeursExerciceCourant > 0
                    ? Math.round((cotisationsPremierExercice / c.payeursExerciceCourant) * 1e6) / 1e6
                    : 0,
                ...produitsMoyens.slice(1),
            ],
        },
        ressources,
        charges: { mode: 'global', progression: SCENARIO_3.progressionCharges },
        trajectoireSalariale: 'grille',
        hypothesesSalariales: [],
        lignesExceptionnelles: [],
        commentaire: null,
    };
}
//# sourceMappingURL=scenario-modele.js.map